import React, { useState, useEffect } from "react";
import axiosInstance from '../../../axiosInstance';
import { fetchSubFormsDropdownValues } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Table from 'apollo-react/components/Table';
import Modal from 'apollo-react/components/Modal';
import Typography from 'apollo-react/components/Typography';
import AutocompleteInput from "../../common/Form/autocompleteInput";
import TableActions from "../../common/DataTable/TableActions";

import { Formik, Form, Field } from "formik";
import Button from "../../common/Button";
import Grid from "apollo-react/components/Grid";
import SwitchInput from "../../common/Form/switchInput";
import TextInput from "../../common/Form/textInput";
import SelectInput from "../../common/Form/selectInput";
import Checkbox from 'apollo-react/components/Checkbox';
import CheckboxGroup from 'apollo-react/components/CheckboxGroup';

const FormsCreate = ({ persona, label, options, value, onChange, multiple }) => {
  const [formsTemplate, setFormsTemplate] = useState([]);
  const [subforms, setSubforms] = useState([]);
  const [subformsRows, setSubformsRows] = useState([]);
  const [draggedRowIndex, setDraggedRowIndex] = useState();
  const [hoveredRowIndex, setHoveredRowIndex] = useState();
  const [discardModal, setDiscardModal] = useState(false);

  const [forms, setForms] = useState({
    name: "",
    description: "",
    published: false,
    subforms: [],
    template: false,
    has_product_details: false,
    has_manufacturers_site: false,
  });
  const [includeSubforms, setIncludeSubforms] = useState([]);
  const [resp, setResp] = useState("");
  const [t] = useTranslation();
  let navigate = useNavigate();

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [localOptions, setLocalOptions] = useState(options);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === index) return;

    const reorderedOptions = [...localOptions];
    const [draggedItem] = reorderedOptions.splice(draggedIndex, 1);
    reorderedOptions.splice(index, 0, draggedItem);

    setLocalOptions(reorderedOptions);
    setDraggedIndex(index);
  };

  const handleDrop = () => {
    onChange(null, localOptions); // Pass reordered options to parent
    setDraggedIndex(null);
  };

  const handleFormChange = (name, value) => {
    if (name === "form_id") {
      const selectedForm = formsTemplate.find((form) => form.id === Number(value));

      setForms({ ...forms, [name]: value, subforms: selectedForm.subforms });
      setSubformsRows(selectedForm.subforms);
    } else {
      setForms({
        ...forms,
        [name]: value,
      });
    }
  };

  const handleIncludeSubformsChange = (e) => {
    const selectedValues = e.target.value;
    setIncludeSubforms(selectedValues);

    // update form state with boolean values
    setForms({
      ...forms,
      has_product_details: selectedValues.includes('products'),
      has_manufacturers_site: selectedValues.includes('manufacturers'),
    });
  };

  const getFormsTemplate = async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_SERVER_URL}/api/forms-template`, { withCredentials: true });
      setFormsTemplate(
        response.data.map((form) => ({
          name: form.name,
          description: form.description,
          published: form.published,
          id: form.id,
          template: 0,
          subforms: form.subforms,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getSubforms = async () => {
    try {
      const { data } = await fetchSubFormsDropdownValues();
      setSubforms(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFormSubmit = (event) => {
    if (forms.name === "") {
      toast.error(t("Form Name is required!"), { position: "top-center" });
      return;
    }

    const updatedForms = { ...forms };
    updatedForms.subforms = subformsRows.map((subform, index) => ({ ...subform, order: index }));
    delete forms.id;

    const url = forms.template ? "/api/forms-template" : "/api/forms";
    axiosInstance
      .post(url, updatedForms)
      .then(() => {
        setResp(t("Form saved"));
        toast.success(t("Form Saved Successfully!"), { position: "top-center" });
        navigate(-1);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    getFormsTemplate();
    getSubforms();
  }, []);

  const renderBasicFormInfo = () => (
    <div>
      <Field name="name">
        {({ field, meta }) => (
          <TextInput
            {...field}
            meta={meta}
            label={"Form name"}
            value={forms?.name || ""}
            onChange={(value) => handleFormChange("name", value)}
            required
          />
        )}
      </Field>

      <Field name="description">
        {({ field, meta }) => (
          <TextInput
            {...field}
            meta={meta}
            label={"Form Description"}
            value={forms?.description}
            onChange={(value) => handleFormChange("description", value)}
            multiline={true}
            textareaWidth={"100%"}
          />
        )}
      </Field>
    </div>
  );

  const ActionCell = ({ row }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <TableActions actions={[{text: 'Delete', color: "secondary", style: {
          border: "solid 1px #D9D9D9",
        }, onClick: (row) => {
          const updatedSubForms = subformsRows.filter((subform) => subform.id !== row.id);
          setSubformsRows(updatedSubForms);
          setForms({ ...forms, subforms: updatedSubForms });
        }}]} row={row} />
      </div>
    );
  };

  return (
    <div
      className="content"
      style={{
        border: "solid 1px #E9E9E9",
        padding: "24px",
        borderRadius: "5px",
      }}
    >
      {resp && <p>{resp}</p>}

      <Grid item md={12}>
        <Formik initialValues={forms} enableReinitialize={true} onSubmit={handleFormSubmit}>
          <Form>
            {renderBasicFormInfo()}

            <Field name="form_id">
              {({ field, meta }) => (
                <SelectInput
                  {...field}
                  meta={meta}
                  label={"Clone predefined Forms"}
                  options={
                    formsTemplate
                      ? formsTemplate.map((form) => ({
                          key: form.id,
                          value: form.id,
                          label: `${form.name}`,
                        }))
                      : []
                  }
                  onChange={(e, value) => {
                    handleFormChange("form_id", e);
                  }}
                  value={forms.form_id}
                />
              )}
            </Field>

            <Field name="subforms">
								{({ field, meta }) => (
									<AutocompleteInput
										{...field}
										meta={meta}
										label={"Sub forms"}
										options={subforms}
										onChange={(e, value) => {
											handleFormChange("subforms", e);
											setSubformsRows(e);
										}}
										// required={true}
										multiple={true}
										value={forms?.subforms}
									/>
								)}
							</Field>

            <Field name="include_subforms">
              {({ field, meta }) => (
                <CheckboxGroup
                  {...field}
                  label="Include Products or Manufacturers Subforms:"
                  helperText="select which additional subforms to include in this form"
                  value={includeSubforms}
                  onChange={handleIncludeSubformsChange}
                  style={{ marginTop: "2px", marginBottom: "2px" }}
                >
                  <Checkbox value="products" label="Products Details" />
                  <Checkbox value="manufacturers" label="Manufacturers Site" />
                </CheckboxGroup>
              )}
            </Field>

            <Typography variant="bodySmall" emphasis="low" gutterBottom style={{ marginTop: "35px", textDecoration: 'underline' }}>{t("subforms_arrange")}</Typography>
							<Table
								columns={[
									{
										header: "Sub forms",
										accessor: "label",
									},
                  {
                    accessor: 'action',
                    customCell: ActionCell,
                    align: 'right',
                  },
								]}
								rows={subformsRows}
								hidePagination
								rowId="id"
								rowProps={{
									draggable: true,
									style: { cursor: "grab" },
									onDragStart: (e) => {
										const row = e.target.closest("tr");
										const children = Array.from(
											e.target.closest("tbody").children
										);
										setDraggedRowIndex(children.indexOf(row));
										// console.log('draggedRowIndex', children.indexOf(row));
									},
									onDragOver: (e) => {
										e.preventDefault();
										const children = Array.from(
											e.target.closest("tbody").children
										);
										const hoveredRow = e.target.closest("tr");
										setHoveredRowIndex(children.indexOf(hoveredRow));
									},
									onDrop: () => {
										// console.log('hoveredRowIndex', hoveredRowIndex);
										const rowsCopy = [...subformsRows];
										const movedRow = subformsRows[draggedRowIndex];
										rowsCopy.splice(draggedRowIndex, 1);
										rowsCopy.splice(hoveredRowIndex, 0, movedRow);
										setSubformsRows(rowsCopy);
										setForms({...forms, "subforms": rowsCopy});
										setHoveredRowIndex(null);
										setDraggedRowIndex(null);
									},
								}}
							/>

            <Field name="template">
              {({ field, meta }) => (
                <SwitchInput
                  {...field}
                  meta={meta}
                  label={t("Save as Template")}
                  checked={forms.template === 1}
                  value={forms?.template}
                  onChange={(checked) => {
                    handleFormChange("template", checked);
                  }}
                  style={{ marginTop: "16px", marginBottom: "8px" }}
                />
              )}
            </Field>

            <Field name="published">
              {({ field, meta }) => (
                <SwitchInput
                  {...field}
                  meta={meta}
                  label={t("Publish")}
                  checked={forms.published === 1}
                  value={forms?.published}
                  onChange={(checked) => {
                    handleFormChange("published", checked);
                  }}
                  style={{ marginTop: "16px" }}
                />
              )}
            </Field>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "50px" }}>
              <Button
                color="primary"
                onClick={() => setDiscardModal(true)}
                style={{
                  padding: "12px 22px",
                  width: "157px",
                  height: "40px",
                  marginRight: "24px",
                }}
                text={t("Discard")}
              />

              <Button
                color="primary"
                type="submit"
                style={{
                  padding: "12px 22px",
                  width: "157px",
                  height: "40px",
                }}
                text={t("Save")}
              />
            </div>
          </Form>
        </Formik>
      </Grid>

      <Modal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: "Confirm",
            onClick: () => {
              setForms({
                name: "",
                description: "",
                published: false,
                subforms: [],
                template: false,
                has_product_details: false,
                has_manufacturers_site: false,
              });
              setSubformsRows([]);
              setIncludeSubforms([]);
              setDiscardModal(false);
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </div>
  );
};

export default FormsCreate;